import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SeoScript from '@/components/base/SeoScript';
import { featuredComics, categories, latestUpdates } from '@/mocks/comics';
import type { ComicItem } from '@/mocks/comics';

type FilterKey = 'all' | 'hot' | 'new' | 'completed';

const filters: { key: FilterKey; label: string }[] = [
  { key: 'all', label: '全部' },
  { key: 'hot', label: '热门' },
  { key: 'new', label: '新作' },
  { key: 'completed', label: '完结' },
];

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeFilter, setActiveFilter] = useState<FilterKey>('all');

  useEffect(() => {
    setActiveFilter('all');
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [slug]);

  const categoryInfo = categories.find((c) => c.slug === slug) || categories[0];

  const allComics: ComicItem[] = [...featuredComics, ...latestUpdates];
  const categoryComics = allComics.filter((c) => c.categorySlug === slug);

  const filteredComics = ((): ComicItem[] => {
    switch (activeFilter) {
      case 'hot':
        return [...categoryComics].sort((a, b) => {
          const va = parseInt(a.views.replace(/[^0-9]/g, ''), 10);
          const vb = parseInt(b.views.replace(/[^0-9]/g, ''), 10);
          return vb - va;
        });
      case 'new':
        return [...categoryComics].sort((a, b) => a.latestChapter - b.latestChapter);
      case 'completed':
        return categoryComics.filter((c) => c.status === '已完结');
      default:
        return categoryComics;
    }
  })();

  const breadcrumbList = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'http://zzxcdell.com/' },
      { '@type': 'ListItem', position: 2, name: categoryInfo.name, item: `http://zzxcdell.com/category/${slug}` },
    ],
  };

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: `${categoryInfo.name} - 韩漫画网`,
    description: `韩漫画网${categoryInfo.name}分类，收录${categoryInfo.count}部韩漫作品，下拉式无遮挡在线阅读。`,
    url: `http://zzxcdell.com/category/${slug}`,
    breadcrumb: { '@id': '#breadcrumb' },
  };

  useEffect(() => {
    document.title = `${categoryInfo.name} - 韩漫画网 | 无遮挡韩漫下拉式在线`;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', `韩漫画网${categoryInfo.name}分类，收录${categoryInfo.count}部韩漫作品。下拉式无遮挡在线阅读，加载迅速零干扰。`);
    }
    const metaKw = document.querySelector('meta[name="keywords"]');
    if (metaKw) {
      metaKw.setAttribute('content', `${categoryInfo.name},韩漫,韩漫画网,下拉式免费,无遮挡韩漫,在线漫画`);
    }
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `http://zzxcdell.com/category/${slug}`);
    }
  }, [slug, categoryInfo]);

  return (
    <div className="min-h-screen bg-background-50">
      <SeoScript data={breadcrumbList} />
      <SeoScript data={webPageSchema} />
      <Navbar />

      {/* Breadcrumb */}
      <div className="pt-20 pb-4 px-6 max-w-screen-xl mx-auto">
        <nav aria-label="面包屑导航" className="flex items-center gap-2 text-xs">
          <Link to="/" className="text-foreground-500 hover:text-foreground-200 transition-colors">首页</Link>
          <i className="ri-arrow-right-s-line text-foreground-600"></i>
          <span className="text-foreground-300">{categoryInfo.name}</span>
        </nav>
      </div>

      {/* Header */}
      <div className="px-6 max-w-screen-xl mx-auto mb-12">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Left: Category Image */}
          <div className="lg:w-1/2">
            <div className="relative rounded-xl overflow-hidden aspect-[16/10]">
              <img
                src={categoryInfo.image}
                alt={categoryInfo.name}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
            </div>
          </div>

          {/* Right: Info */}
          <div className="lg:w-1/2 flex flex-col justify-between">
            <div>
              <h1 className="font-heading text-3xl md:text-5xl font-black text-foreground-50 mb-4">
                {categoryInfo.name}
              </h1>
              <div className="flex flex-wrap items-center gap-3 mb-6">
                {filters.map((f) => (
                  <button
                    key={f.key}
                    onClick={() => setActiveFilter(f.key)}
                    className={`px-4 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all cursor-pointer ${
                      activeFilter === f.key
                        ? 'border border-primary-500/40 text-primary-400 bg-primary-500/10'
                        : 'text-foreground-400 hover:text-foreground-200'
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="bg-background-100 rounded-xl p-6 flex items-center gap-6">
              <div>
                <span className="inline-block px-2.5 py-1 rounded-md bg-primary-500/20 text-primary-400 text-xs font-medium mb-2">
                  共收录
                </span>
                <h3 className="text-lg font-bold text-foreground-50">{categoryInfo.count}部作品</h3>
                <p className="text-sm text-foreground-500 mt-2 leading-relaxed">
                  精选{categoryInfo.name}类韩漫，每日更新不停歇，下拉式无遮挡阅读，畅享沉浸体验。
                </p>
              </div>
              <div className="flex-shrink-0 w-20 h-20 rounded-xl bg-background-200 flex items-center justify-center">
                <i className={`${categoryInfo.icon} text-3xl text-primary-500`}></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Comics Grid */}
      <section className="py-8 px-6 max-w-screen-xl mx-auto">
        <div className="flex items-end justify-between mb-10">
          <div>
            <h2 className="font-heading text-2xl font-bold text-foreground-50">
              {filters.find((f) => f.key === activeFilter)?.label}作品
            </h2>
            <p className="text-sm text-foreground-500 mt-1">共{filteredComics.length}部作品</p>
          </div>
        </div>

        {filteredComics.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 mx-auto rounded-full bg-background-100 flex items-center justify-center mb-4">
              <i className="ri-emotion-sad-line text-2xl text-foreground-500"></i>
            </div>
            <p className="text-sm text-foreground-500">该分类下暂无作品</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {filteredComics.map((comic) => (
              <Link
                key={comic.id}
                to={`/comic/${comic.slug}`}
                className="group relative rounded-lg overflow-hidden bg-background-100 cursor-pointer"
              >
                <div className="relative aspect-[3/4] overflow-hidden">
                  <img
                    src={comic.coverImage}
                    alt={comic.title}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-background-950/80 backdrop-blur-sm text-xs font-medium text-foreground-50 whitespace-nowrap">
                    {comic.status}
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
                <div className="p-3 md:p-4">
                  <h3 className="text-sm md:text-base font-bold text-foreground-50 truncate">{comic.title}</h3>
                  <p className="text-xs text-foreground-500 mt-1">{comic.status === '连载中' ? `更新至${comic.latestChapter}话` : '已完结'}</p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
}